import type { LucideIcon } from "lucide-react"
import {
  Activity,
  Briefcase,
  Building2,
  Calculator,
  CalendarCheck,
  CalendarDays,
  LayoutDashboard,
  Package,
  Shield,
  Target,
} from "lucide-react"

import type { useNavPermissions } from "@/hooks/use-nav-permissions"
import {
  accountingExpensesPath,
  accountingPayrollIndexPath,
  accountingReportsExpensesPath,
  accountingReportsServiceItemsPath,
  accountingSubcontractorsPath,
  adminActivityTypesPath,
  adminBgDocumentTypesPath,
  adminClientTypesPath,
  adminContactRolesPath,
  adminContactTypesPath,
  adminCustomerDocumentTypesPath,
  adminDefaultRatesPath,
  adminEquipmentTypesPath,
  adminEvidenceTypesPath,
  adminExpenseTypesPath,
  adminInvestigationTypesPath,
  adminLogTypesPath,
  adminMarketingActivityTypesPath,
  adminMarketingCategoriesPath,
  adminMessagesPath,
  adminPaymentTypesPath,
  adminReportCategoriesPath,
  adminResourceTypesPath,
  adminServiceItemTypesPath,
  adminSiteAdminPath,
  adminSubjectTypesPath,
  adminTimeZonesPath,
  adminTodoCategoriesPath,
  adminVendorTypeGroupsPath,
  adminVendorTypesPath,
  availabilityPath,
  calendarPath,
  caseLogReviewsPath,
  casesPath,
  companyDocumentsPath,
  companyPhoneBookIndexPath,
  companyResourcesPath,
  companySignaturesPath,
  companyVendorsPath,
  customersPath,
  dashboardPath,
  employeesActivityPath,
  employeesActivityUnsubmittedPath,
  employeesPath,
  equipmentIndexPath,
  gpsDevicesPath,
  historyAccountingExpensesPath,
  historyAccountingPayrollIndexPath,
  marketingActivitiesPath,
  permissionsEmployeesPath,
  ptoRequestsPath,
  reportsCaseActivityIndexPath,
  reportsClientLogsPath,
  reportsEmailChangesPath,
  reportsMarketingClientTrendsPath,
  reportsMarketingMailingListsPath,
  reportsMarketingNewCasesPath,
  reportsMarketingReferralMailingIndexPath,
  reportsPendingApprovalIndexPath,
  reportsPendingPreparationIndexPath,
  reportsReferralsByContactIndexPath,
  reportsReferralsPath,
  reviewTimeExpensesPath,
  summaryAccountingPayrollIndexPath,
  teamActivityPath,
  teamReportsPath,
  teamSchedulePath,
  teamSignaturePath,
  teamTasksPath,
} from "@/routes"

// ─── Icon Nav Types & Data ──────────────────────────────────────────

export interface IconNavItem {
  label: string
  href: string
  icon: LucideIcon
  /** Additional URL prefixes that should activate this icon */
  matchPrefixes?: string[]
}

export const navItems: IconNavItem[] = [
  {
    label: "Home",
    href: dashboardPath(),
    icon: LayoutDashboard,
    matchPrefixes: ["/team", "/dashboard"],
  },
  { label: "Customers", href: customersPath(), icon: Building2 },
  {
    label: "Cases",
    href: casesPath(),
    icon: Briefcase,
    matchPrefixes: ["/cases"],
  },
  {
    label: "Calendar",
    href: calendarPath(),
    icon: CalendarDays,
    matchPrefixes: ["/calendar"],
  },
  {
    label: "Availability",
    href: availabilityPath(),
    icon: CalendarCheck,
    matchPrefixes: ["/availability"],
  },
  {
    label: "Activity",
    href: reportsCaseActivityIndexPath(),
    icon: Activity,
    matchPrefixes: [
      "/pto_requests",
      "/employees/activity",
      "/reports",
      "/case_log_reviews",
      "/review",
    ],
  },
  {
    label: "Marketing",
    href: marketingActivitiesPath(),
    icon: Target,
    matchPrefixes: ["/marketing_activities", "/marketing_todos"],
  },
  {
    label: "Accounting",
    href: accountingExpensesPath(),
    icon: Calculator,
    matchPrefixes: ["/accounting"],
  },
  {
    label: "Equipment",
    href: equipmentIndexPath(),
    icon: Package,
    matchPrefixes: ["/equipment", "/gps_devices"],
  },
  {
    label: "Company",
    href: companyResourcesPath(),
    icon: Briefcase,
    matchPrefixes: ["/company"],
  },
  {
    label: "Admin",
    href: adminActivityTypesPath(),
    icon: Shield,
    matchPrefixes: ["/admin", "/employees", "/vendors"],
  },
]

/** Find the single active nav item using longest-prefix-match */
export function findActiveIndex(
  items: IconNavItem[],
  currentPath: string,
): number {
  let bestIndex = -1
  let bestLength = 0

  for (let i = 0; i < items.length; i++) {
    const item = items[i]

    // Home special case — exact match only
    if (item.href === "/" || item.href === dashboardPath()) {
      if (currentPath === "/" || currentPath === dashboardPath()) {
        const len = currentPath.length
        if (len > bestLength) {
          bestIndex = i
          bestLength = len
        }
      }
    }

    // Check matchPrefixes
    if (item.matchPrefixes) {
      for (const prefix of item.matchPrefixes) {
        if (currentPath.startsWith(prefix) && prefix.length > bestLength) {
          bestIndex = i
          bestLength = prefix.length
        }
      }
    }

    // Check href
    if (
      !item.href.startsWith("#") &&
      item.href !== "/" &&
      currentPath.startsWith(item.href)
    ) {
      if (item.href.length > bestLength) {
        bestIndex = i
        bestLength = item.href.length
      }
    }
  }

  return bestIndex
}

export const iconNavPermissions: Record<
  string,
  (perms: ReturnType<typeof useNavPermissions>) => boolean
> = {
  Customers: (p) => p.canViewCustomers,
  Calendar: (p) => p.canViewCalendar,
  Activity: (p) => p.hasActivityAccess,
  Marketing: (p) => p.hasMarketingAccess,
  Accounting: (p) => p.hasAccountingAccess,
  Equipment: (p) => p.isAdmin || p.isFullAccess,
  Admin: (p) => p.hasAdminAccess,
}

// ─── Secondary Nav Types & Data ─────────────────────────────────────

export interface NavLink {
  label: string
  href: string
}

export interface NavGroup {
  label: string
  children: NavLink[]
}

export type NavEntry = NavLink | NavGroup

export interface NavSection {
  title: string
  items: NavEntry[]
}

export function isGroup(entry: NavEntry): entry is NavGroup {
  return "children" in entry
}

function nextWeekStart(): string {
  const d = new Date()
  d.setDate(d.getDate() - d.getDay() + 7) // Next Sunday
  return d.toISOString().split("T")[0]
}

function lastWeekStart(): string {
  const d = new Date()
  d.setDate(d.getDate() - d.getDay() - 7) // Last Sunday
  return d.toISOString().split("T")[0]
}

function nextMonthParams(): string {
  const d = new Date()
  const m = d.getMonth() === 11 ? 1 : d.getMonth() + 2
  const y = d.getMonth() === 11 ? d.getFullYear() + 1 : d.getFullYear()
  return `year=${y}&month=${m}`
}

function lastMonthParams(): string {
  const d = new Date()
  const m = d.getMonth() === 0 ? 12 : d.getMonth()
  const y = d.getMonth() === 0 ? d.getFullYear() - 1 : d.getFullYear()
  return `year=${y}&month=${m}`
}

export const sections: Record<string, NavSection> = {
  home: {
    title: "Home",
    items: [
      { label: "Pending Tasks", href: teamTasksPath() },
      { label: "Schedule", href: teamSchedulePath() },
      { label: "Time & Expenses", href: teamActivityPath() },
      { label: "Email Signature", href: teamSignaturePath() },
      { label: "My Reports", href: teamReportsPath() },
    ],
  },
  customers: {
    title: "Customers",
    items: [
      { label: "Recent Activity", href: customersPath() },
      {
        label: "Most Active Cases",
        href: `${customersPath()}?sort=activeCases`,
      },
      {
        label: "Most Cases - Past 12 mo.",
        href: `${customersPath()}?sort=cases12mo`,
      },
      {
        label: "Most Cases - Past 24 mo.",
        href: `${customersPath()}?sort=cases24mo`,
      },
      { label: "Total Cases", href: `${customersPath()}?sort=totalCases` },
    ],
  },
  cases: {
    title: "Cases",
    items: [
      { label: "Active Cases", href: casesPath() },
      { label: "Pending Report", href: `${casesPath()}?status=3` },
      { label: "Pending Final Payment", href: `${casesPath()}?status=4` },
      { label: "Newest Potential Cases", href: `${casesPath()}?status=0` },
    ],
  },
  calendar: {
    title: "Calendar",
    items: [
      { label: "This Week", href: calendarPath() },
      {
        label: "Next Week",
        href: `${calendarPath()}?week_start=${nextWeekStart()}`,
      },
      {
        label: "Last Week",
        href: `${calendarPath()}?week_start=${lastWeekStart()}`,
      },
    ],
  },
  availability: {
    title: "Availability",
    items: [
      { label: "This Month", href: availabilityPath() },
      {
        label: "Next Month",
        href: `${availabilityPath()}?${nextMonthParams()}`,
      },
      {
        label: "Last Month",
        href: `${availabilityPath()}?${lastMonthParams()}`,
      },
    ],
  },
  activity: {
    title: "Activity",
    items: [
      { label: "Pending Reports", href: reportsPendingApprovalIndexPath() },
      {
        label: "Prepare Reports",
        href: reportsPendingPreparationIndexPath(),
      },
      { label: "Search Reports", href: "#search-reports" },
      {
        label: "Reports",
        children: [
          { label: "Case Activity", href: reportsCaseActivityIndexPath() },
          { label: "Client Trends", href: "#client-trends" },
          { label: "Email Change Log", href: reportsEmailChangesPath() },
          { label: "Log Entries", href: reportsClientLogsPath() },
          {
            label: "Mailing Lists",
            href: reportsMarketingMailingListsPath(),
          },
          { label: "New Referrals", href: reportsReferralsPath() },
          {
            label: "Referrals by Contact",
            href: reportsReferralsByContactIndexPath(),
          },
          { label: "Referrals by Firm", href: "#referrals-by-firm" },
        ],
      },
      {
        label: "Time & Expenses",
        children: [
          {
            label: "Awaiting Approval",
            href: reviewTimeExpensesPath(),
          },
          { label: "Activity History", href: employeesActivityPath() },
          {
            label: "Unsubmitted",
            href: employeesActivityUnsubmittedPath(),
          },
          {
            label: "Search Expenses",
            href: accountingReportsExpensesPath(),
          },
        ],
      },
      { label: "Time Off Requests", href: ptoRequestsPath() },
      { label: "Reviews", href: caseLogReviewsPath() },
    ],
  },
  marketing: {
    title: "Marketing",
    items: [
      { label: "Projects & Events", href: marketingActivitiesPath() },
      { label: "Categories", href: adminMarketingCategoriesPath() },
      {
        label: "Reports",
        children: [
          { label: "Case Activity", href: reportsMarketingNewCasesPath() },
          {
            label: "Client Trends",
            href: reportsMarketingClientTrendsPath(),
          },
          { label: "Email Change Log", href: reportsEmailChangesPath() },
          { label: "Log Entries", href: reportsClientLogsPath() },
          {
            label: "Mailing Lists",
            href: reportsMarketingMailingListsPath(),
          },
          {
            label: "New Referrals",
            href: reportsMarketingReferralMailingIndexPath(),
          },
          {
            label: "Referrals by Contact",
            href: reportsReferralsByContactIndexPath(),
          },
          { label: "Referrals by Firm", href: reportsReferralsPath() },
        ],
      },
    ],
  },
  accounting: {
    title: "Accounting",
    items: [
      {
        label: "Expenses",
        children: [
          { label: "Pay Expenses", href: accountingExpensesPath() },
          { label: "History", href: historyAccountingExpensesPath() },
          { label: "Subcontractors", href: accountingSubcontractorsPath() },
        ],
      },
      {
        label: "Payroll",
        children: [
          { label: "Summary", href: summaryAccountingPayrollIndexPath() },
          { label: "Current", href: accountingPayrollIndexPath() },
          { label: "History", href: historyAccountingPayrollIndexPath() },
        ],
      },
      {
        label: "Reports",
        children: [
          {
            label: "Service Items",
            href: accountingReportsServiceItemsPath(),
          },
        ],
      },
      { label: "Search Expenses", href: accountingReportsExpensesPath() },
      { label: "YTD Case Summary", href: "#ytd-case-summary" },
    ],
  },
  company: {
    title: "Company",
    items: [
      { label: "Bios & Flyers", href: companyResourcesPath() },
      { label: "Documents", href: companyDocumentsPath() },
      { label: "Phone Book", href: companyPhoneBookIndexPath() },
      { label: "Signatures", href: companySignaturesPath() },
      { label: "Subcontractors", href: companyVendorsPath() },
    ],
  },
  equipment: {
    title: "Equipment",
    items: [
      { label: "Equipment List", href: equipmentIndexPath() },
      { label: "GPS Devices", href: gpsDevicesPath() },
    ],
  },
  admin: {
    title: "Admin",
    items: [
      { label: "Employees", href: employeesPath() },
      { label: "Permissions", href: permissionsEmployeesPath() },
      { label: "Default Rates", href: adminDefaultRatesPath() },
      { label: "Site Admin", href: adminSiteAdminPath() },
      {
        label: "Lookup Tables",
        children: [
          { label: "Activity Types", href: adminActivityTypesPath() },
          { label: "BG Document Types", href: adminBgDocumentTypesPath() },
          { label: "Client Types", href: adminClientTypesPath() },
          { label: "Contact Roles", href: adminContactRolesPath() },
          { label: "Contact Types", href: adminContactTypesPath() },
          {
            label: "Customer Doc Types",
            href: adminCustomerDocumentTypesPath(),
          },
          { label: "Equipment Types", href: adminEquipmentTypesPath() },
          { label: "Evidence Types", href: adminEvidenceTypesPath() },
          { label: "Expense Types", href: adminExpenseTypesPath() },
          {
            label: "Investigation Types",
            href: adminInvestigationTypesPath(),
          },
          { label: "Log Types", href: adminLogTypesPath() },
          {
            label: "Marketing Activity Types",
            href: adminMarketingActivityTypesPath(),
          },
          {
            label: "Marketing Categories",
            href: adminMarketingCategoriesPath(),
          },
          { label: "Messages", href: adminMessagesPath() },
          { label: "Payment Types", href: adminPaymentTypesPath() },
          { label: "Report Categories", href: adminReportCategoriesPath() },
          { label: "Resource Types", href: adminResourceTypesPath() },
          {
            label: "Service Item Types",
            href: adminServiceItemTypesPath(),
          },
          { label: "Subject Types", href: adminSubjectTypesPath() },
          { label: "Time Zones", href: adminTimeZonesPath() },
          { label: "Todo Categories", href: adminTodoCategoriesPath() },
          {
            label: "Vendor Type Groups",
            href: adminVendorTypeGroupsPath(),
          },
          { label: "Vendor Types", href: adminVendorTypesPath() },
        ],
      },
      { label: "Reports", href: "#admin-reports" },
    ],
  },
}

/** URL prefix → section key mapping (checked in order, first match wins) */
export const routeMatches: [string[], string][] = [
  [["/customers"], "customers"],
  [["/cases"], "cases"],
  [["/calendar"], "calendar"],
  [["/availability"], "availability"],
  [
    [
      "/marketing_activities",
      "/marketing_todos",
      "/reports/marketing",
      "/reports/email_changes",
      "/reports/client_logs",
      "/reports/referrals_by_contact",
      "/reports/referrals",
    ],
    "marketing",
  ],
  [
    [
      "/pto_requests",
      "/employees/activity",
      "/reports",
      "/case_log_reviews",
      "/review",
    ],
    "activity",
  ],
  [["/accounting"], "accounting"],
  [["/company"], "company"],
  [["/equipment", "/gps_devices", "/equipment_logs"], "equipment"],
  [["/admin", "/employees"], "admin"],
  [["/dashboard", "/team", "/"], "home"],
]

export function detectSection(currentPath: string): string {
  for (const [prefixes, sectionKey] of routeMatches) {
    for (const prefix of prefixes) {
      if (prefix === "/") {
        if (currentPath === "/") return sectionKey
      } else if (currentPath.startsWith(prefix)) {
        return sectionKey
      }
    }
  }
  return "home"
}

export function isActiveLink(href: string, currentUrl: string): boolean {
  if (href.startsWith("#")) return false
  // If href has query params, compare against full URL
  if (href.includes("?")) return currentUrl === href
  // For base paths, strip query string and compare
  const currentPath = currentUrl.split("?")[0]
  if (href === currentPath) return true
  // For index paths, also match if we're on a nested page
  if (href !== "/" && currentPath.startsWith(href)) return true
  return false
}
